import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import * as Sentry from '@sentry/react';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { applyTheme, type GenderPreset } from '../theme';
import { ApiError, EMAIL_UNVERIFIED_EVENT, LEGAL_ACCEPTANCE_REQUIRED_EVENT, ACCOUNT_DELETION_PENDING_EVENT } from '../api';
import { AuthProvider, useAuth } from '../auth/AuthProvider';
import { BillingProvider } from '../billing/BillingProvider';
import { I18nProvider, useT } from '../i18n';
import { authMode } from '../auth/firebase';
import { AppShell, type TabId } from '../components/chrome/AppShell';
import { RateLimitToast } from '../components/chrome/RateLimitToast';
import { ProfileSheet } from '../components/chrome/ProfileSheet';
import { SettingsSheet } from '../components/chrome/SettingsSheet';
import { NotificationsSheet } from '../components/chrome/NotificationsSheet';
import { Today } from '../components/today/Today';
import { Dashboard } from '../components/today/Dashboard';
import { Activities } from '../components/activities/Activities';
import { MilestoneList } from '../components/milestones/MilestoneList';
import { Timeline } from '../components/timeline/Timeline';
import { JourneyTimeline } from '../components/journey/JourneyTimeline';
import { StatusSheet } from '../components/status/StatusSheet';
import { PhotoUploadSheet } from '../components/media/PhotoUploadSheet';
import { clearOnboardingDraft } from '../components/onboarding/onboardingData';
import { EncryptionProvider } from '../crypto/EncryptionProvider';
import { BabyFormSheet } from '../components/profile/BabyFormSheet';
import type { BabyFormData } from '../components/profile/BabyFormSheet';
import { Splash } from './Splash';
import { babies as fixtureBabies } from '../data/baby';
import { queryClient } from '../data/queryClient';

// Code-split heavy secondary routes and sub-views to optimize initial bundle size
const OnboardingFlow = lazy(() => import('../components/onboarding/OnboardingFlow').then((m) => ({ default: m.OnboardingFlow })));
const MilestoneCatchup = lazy(() => import('../components/onboarding/MilestoneCatchup').then((m) => ({ default: m.MilestoneCatchup })));
const EmailVerificationScreen = lazy(() => import('./EmailVerificationScreen').then((m) => ({ default: m.EmailVerificationScreen })));
const LegalAcceptanceScreen = lazy(() => import('./LegalAcceptanceScreen').then((m) => ({ default: m.LegalAcceptanceScreen })));
const AccountDeletionScreen = lazy(() => import('./AccountDeletionScreen').then((m) => ({ default: m.AccountDeletionScreen })));
const Growth = lazy(() => import('../components/growth/Growth').then((m) => ({ default: m.Growth })));
const AiChat = lazy(() => import('../components/ai/AiChat').then((m) => ({ default: m.AiChat })));
const Leaps = lazy(() => import('../components/leaps/Leaps').then((m) => ({ default: m.Leaps })));
const LeapDetail = lazy(() => import('../components/leaps/LeapDetail').then((m) => ({ default: m.LeapDetail })));
const BabyMediaGallery = lazy(() => import('../components/media/BabyMediaGallery').then((m) => ({ default: m.BabyMediaGallery })));
const MilestoneDetail = lazy(() => import('../components/detail/MilestoneDetail').then((m) => ({ default: m.MilestoneDetail })));
const ActivityDetail = lazy(() => import('../components/activities/ActivityDetail').then((m) => ({ default: m.ActivityDetail })));
const ActivityCategoryList = lazy(() => import('../components/activities/ActivityCategoryList').then((m) => ({ default: m.ActivityCategoryList })));
import { qk } from '../data/keys';
import {
  useAcceptInvite,
  useBabyMilestones,
  useCreateBaby,
  useMe,
  useUpdateBaby,
  useUpdateMilestoneStatus,
} from '../data/queries';
import { useActiveBaby } from '../data/activeBaby';
import { logAnalyticsEvent, setAnalyticsUserId, setAnalyticsUserProperties } from '../auth/analytics';
import { clearPendingInvite, getPendingInvite } from '../data/pendingInvite';
import { JoinBabySheet } from '../components/profile/JoinBabySheet';
import { dateChoiceForIso, isoForDateChoice, rowToBaby, rowToMilestone, serverStatus, todayIso, uiStatus } from '../data/derive';
import type { BabyEventRow, BabyRow } from '../data/dto';
import type { MilestoneStatus } from '../data/types';

/**
 * App root. Wraps the tree in QueryClientProvider + AuthProvider; everything
 * else lives in Bootstrap so the baby form data captured during onboarding
 * survives the `unauthenticated → authenticated` transition (the
 * OnboardingFlow unmounts the moment that flip happens).
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <BillingProvider>
          <AuthProvider>
            <Bootstrap />
            <RateLimitToast />
          </AuthProvider>
        </BillingProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

/**
 * Holds the "pending baby" form data captured during onboarding plus a flag
 * that says "yes, this auth call was a fresh signup". When auth resolves to
 * `authenticated`, POSTs /api/v1/babies and pushes the new row into the
 * TanStack Query cache so MainApp sees it immediately.
 */
function Bootstrap() {
  const { status } = useAuth();
  const createBaby = useCreateBaby();
  const acceptInvite = useAcceptInvite();
  const qc = useQueryClient();

  const pendingBabyRef = useRef<BabyFormData | null>(null);
  const pendingJoinRef = useRef<string | null>(null);
  const signupAttemptedRef = useRef(false);
  const postedRef = useRef(false);

  // A pending JOIN (from the onboarding "join existing" path) is accepted after
  // auth regardless of signup-vs-signin — an invited person may use a brand new
  // account or an existing one. A pending CREATE only fires for a true signup.
  useEffect(() => {
    if (status !== 'authenticated') return;
    if (postedRef.current) return;

    const joinToken = pendingJoinRef.current;
    if (joinToken) {
      postedRef.current = true;
      if (authMode === 'mock') {
        // eslint-disable-next-line no-console
        console.info('[onboarding] mock auth — skipping POST /invites/{token}/accept.');
        clearPendingInvite();
        return;
      }
      // eslint-disable-next-line no-console
      console.info('[onboarding] POST /invites/{token}/accept');
      acceptInvite.mutate(joinToken, {
        onSuccess: (r) => {
          pendingJoinRef.current = null;
          clearPendingInvite();
          try { localStorage.setItem('babysteps.active_baby_id', r.baby.id); } catch { /* no-op */ }
          qc.invalidateQueries({ queryKey: qk.babies() });
        },
        onError: (e: unknown) => {
          const detail = e instanceof ApiError ? `HTTP ${e.status} ${JSON.stringify(e.body)}` : String(e);
          // eslint-disable-next-line no-console
          console.error('[onboarding] accept-invite failed:', detail);
        },
      });
      return;
    }

    if (!signupAttemptedRef.current) return;
    const baby = pendingBabyRef.current;
    if (!baby) return;

    if (authMode === 'mock') {
      postedRef.current = true;
      // eslint-disable-next-line no-console
      console.info(
        '[onboarding] mock auth mode — skipping POST /api/v1/babies. ' +
          'Set VITE_AUTH_MODE=firebase to test the real backend round-trip.',
      );
      return;
    }

    postedRef.current = true;
    // eslint-disable-next-line no-console
    console.info('[onboarding] POST /api/v1/babies', { name: baby.name, gender: baby.gender });
    createBaby.mutate(
      {
        name: baby.name,
        date_of_birth: baby.dob,
        gender: baby.gender,
      },
      {
        onSuccess: (r) => {
          pendingBabyRef.current = null;
          signupAttemptedRef.current = false;
          clearOnboardingDraft();
          // eslint-disable-next-line no-console
          console.info('[onboarding] baby created', r.baby.id);
          sessionStorage.setItem('just_signed_up_baby_' + r.baby.id, 'true');
          // Make sure the babies list query reflects the new baby even if a
          // GET /babies was in flight.
          qc.invalidateQueries({ queryKey: qk.babies() });
        },
        onError: (e: unknown) => {
          const detail = e instanceof ApiError
            ? `HTTP ${e.status} ${JSON.stringify(e.body)}`
            : e instanceof Error
            ? e.message
            : String(e);
          // eslint-disable-next-line no-console
          console.error('[onboarding] /babies POST failed:', detail);
        },
      },
    );
  }, [status, createBaby, acceptInvite, qc]);

  if (status === 'loading') return <Splash />;
  if (status === 'unauthenticated') {
    // Reset so a subsequent signup can POST again.
    postedRef.current = false;
    return (
      <Suspense fallback={<Splash />}>
        <OnboardingFlow
          onBabyData={(b) => {
            pendingBabyRef.current = b;
          }}
          onJoinData={(token) => {
            pendingJoinRef.current = token;
          }}
          onSignupSubmit={() => {
            signupAttemptedRef.current = true;
          }}
        />
      </Suspense>
    );
  }
  return <MainApp />;
}

/**
 * Authenticated experience. Pulls the user + babies list from the API and
 * picks the active baby (persisted via useActiveBaby). The screen renderers
 * still receive the same Baby view-model they used with fixtures, so no
 * screen needed to change for this phase.
 */
function MainApp() {
  const { signOut } = useAuth();
  const me = useMe();

  const user = me.data?.user;
  const isUnverified = user && user.email_verified_at === null;
  const createdTime = user ? new Date(user.created_at + ' UTC').getTime() : 0;
  const daysOld = (Date.now() - createdTime) / (1000 * 60 * 60 * 24);
  const isBlocked = isUnverified && daysOld > 7;

  const outstandingLegal = me.data?.legal?.filter((d) => d.needs_acceptance) ?? [];
  const needsLegalAcceptance = outstandingLegal.length > 0;
  const needsAccountDeletion = user?.deletion_requested_at != null;

  // A 403 email_unverified mid-session (grace period just expired) refetches
  // /me, which flips isBlocked and swaps in the EmailVerificationScreen. The
  // web already blocks unverified users at launch via the precompute above;
  // this only covers the rare expiry-while-open case. Same idea for a legal
  // document version bumping mid-session, or deletion being requested from
  // another device/session.
  useEffect(() => {
    const onUnverified = () => {
      queryClient.invalidateQueries({ queryKey: qk.me() });
    };
    window.addEventListener(EMAIL_UNVERIFIED_EVENT, onUnverified);
    window.addEventListener(LEGAL_ACCEPTANCE_REQUIRED_EVENT, onUnverified);
    window.addEventListener(ACCOUNT_DELETION_PENDING_EVENT, onUnverified);
    return () => {
      window.removeEventListener(EMAIL_UNVERIFIED_EVENT, onUnverified);
      window.removeEventListener(LEGAL_ACCEPTANCE_REQUIRED_EVENT, onUnverified);
      window.removeEventListener(ACCOUNT_DELETION_PENDING_EVENT, onUnverified);
    };
  }, []);

  const { baby, babies, activeId, setActiveId, loading: babiesLoading } = useActiveBaby(
    !!user && !isBlocked && !needsLegalAcceptance && !needsAccountDeletion
  );
  const createBaby = useCreateBaby();
  const [pendingJoin, setPendingJoin] = useState<string | null>(() => getPendingInvite());

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: String(user.id),
        email: user.email ?? undefined,
      });
      setAnalyticsUserId(String(user.id));
      if (user.email) {
        setAnalyticsUserProperties({ email: user.email });
      }
    } else {
      Sentry.setUser(null);
      setAnalyticsUserId(null);
    }
  }, [user]);

  useEffect(() => {
    if (baby?.id) {
      Sentry.setTag('baby_id', String(baby.id));
      setAnalyticsUserProperties({ active_baby_id: String(baby.id) });
    } else {
      Sentry.setTag('baby_id', undefined);
      setAnalyticsUserProperties({ active_baby_id: null });
    }
  }, [baby?.id]);

  if (me.isLoading || babiesLoading) return <Splash />;
  if (me.isError) {
    return <AuthError error={me.error} onSignOut={signOut} />;
  }

  // Priority matches AuthMiddleware server-side: deletion pending, then
  // email verification, then legal acceptance.
  if (needsAccountDeletion && user) {
    return (
      <Suspense fallback={<Splash />}>
        <AccountDeletionScreen scheduledPurgeAt={user.deletion_scheduled_purge_at} onSignOut={signOut} />
      </Suspense>
    );
  }

  if (isBlocked && user) {
    return (
      <Suspense fallback={<Splash />}>
        <EmailVerificationScreen user={user} onSignOut={signOut} />
      </Suspense>
    );
  }

  if (needsLegalAcceptance && user) {
    return (
      <Suspense fallback={<Splash />}>
        <LegalAcceptanceScreen outstanding={outstandingLegal} onSignOut={signOut} />
      </Suspense>
    );
  }

  if (babies.length === 0 || !baby) {
    return (
      <>
        <NoBabies
          email={me.data?.user.email ?? null}
          onAddBaby={(data) =>
            createBaby.mutate(
              { name: data.name, date_of_birth: data.dob, gender: data.gender },
              { onError: (e) => console.error(e) },
            )
          }
          onJoinBaby={() => setPendingJoin((c) => c ?? '')}
          adding={createBaby.isPending}
        />
        <JoinBabySheet
          open={pendingJoin !== null}
          initialCode={pendingJoin || null}
          onClose={() => {
            clearPendingInvite();
            setPendingJoin(null);
          }}
          onJoined={(id) => {
            clearPendingInvite();
            setPendingJoin(null);
            setActiveId(id);
          }}
        />
      </>
    );
  }

  return (
    <EncryptionProvider key={baby.id!} activeBabyId={baby.id!}>
      <MainAppContent
        baby={baby}
        babies={babies}
        activeId={activeId!}
        setActiveId={setActiveId}
        createBaby={createBaby}
        pendingJoin={pendingJoin}
        setPendingJoin={setPendingJoin}
      />
    </EncryptionProvider>
  );
}

interface MainAppContentProps {
  baby: any;
  babies: BabyRow[];
  activeId: string;
  setActiveId: (id: string) => void;
  createBaby: any;
  pendingJoin: string | null;
  setPendingJoin: React.Dispatch<React.SetStateAction<string | null>>;
}

function MainAppContent({ baby, babies, activeId, setActiveId, createBaby, pendingJoin, setPendingJoin }: MainAppContentProps) {
  const { signOut } = useAuth();
  const me = useMe();
  const qc = useQueryClient();
  const updateBaby = useUpdateBaby();

  // Pick the gender preset from the active baby's row. Falls back to 'neutral'
  // if no baby exists yet.
  const [gender, setGender] = useState<GenderPreset>('neutral');
  useEffect(() => {
    if (baby?.preset) setGender(baby.preset);
  }, [baby?.preset]);
  useEffect(() => {
    applyTheme(gender);
  }, [gender]);

  const [showCatchup, setShowCatchup] = useState(false);
  useEffect(() => {
    if (!baby || !baby.id) return;
    const isFresh = sessionStorage.getItem('just_signed_up_baby_' + baby.id) === 'true';
    const isCompleted = localStorage.getItem('onboarding_catchup_completed_' + baby.id) === 'true';
    if (isFresh && !isCompleted && (baby.ageWeeks >= 4 || baby.ageMonths >= 1.0)) {
      setShowCatchup(true);
    }
  }, [baby?.id, baby?.ageWeeks, baby?.ageMonths]);

  const [tab, setTab] = useState<TabId>('today');
  const [navStack, setNavStack] = useState<Array<
    | { type: 'milestone'; id: string; showTiming?: boolean }
    | { type: 'activity'; id: number }
    | { type: 'leap'; id: number }
    | { type: 'activityCategory'; id: 'play' | 'sing' | 'music' | 'read' | 'excursion' | 'favorites' }
  >>([]);

  useEffect(() => {
    logAnalyticsEvent('screen_view', { screen_name: tab });
  }, [tab]);

  useEffect(() => {
    const lastItem = navStack[navStack.length - 1];
    if (lastItem) {
      logAnalyticsEvent('view_item', {
        item_type: lastItem.type,
        item_id: String(lastItem.id),
      });
    }
  }, [navStack]);
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const openMilestoneDetail = (id: string, showTiming?: boolean) => {
    setShowDashboard(false);
    setNavStack(prev => [...prev, { type: 'milestone', id, showTiming: !!showTiming }]);
  };

  const openActivityDetail = (id: number) => {
    setNavStack(prev => [...prev, { type: 'activity', id }]);
  };

  const openActivityCategory = (id: 'play' | 'sing' | 'music' | 'read' | 'excursion' | 'favorites') => {
    setNavStack(prev => [...prev, { type: 'activityCategory', id }]);
  };

  const openLeapDetail = (id: number) => {
    setNavStack(prev => [...prev, { type: 'leap', id }]);
  };

  const handlePopDetail = () => {
    setNavStack(prev => prev.slice(0, -1));
  };

  // Notification tap → the detail view the event is about. Falls back to the
  // closest list/tab when the payload doesn't carry a concrete entity id
  // (e.g. an aggregated "5 milestones achieved" batch event).
  const handleOpenEvent = (e: BabyEventRow) => {
    setShowNotifications(false);
    setShowMediaGallery(false);
    const p = e.payload ?? {};
    const num = (v: unknown): number | null =>
      typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : null;
    switch (e.event_type) {
      case 'milestone_achieved': {
        const mid = num(p.milestone_id);
        if (mid !== null) openMilestoneDetail(String(mid));
        else setTab('list');
        break;
      }
      case 'activity_completed': {
        const aid = num(p.activity_id);
        if (aid !== null) openActivityDetail(aid);
        else setTab('activities');
        break;
      }
      case 'photo_added': {
        const mid = num(p.milestone_id);
        if (mid !== null) openMilestoneDetail(String(mid));
        else setShowMediaGallery(true);
        break;
      }
      case 'growth_added':
        setNavStack([]);
        setTab('growth');
        break;
      case 'comment_added': {
        const et = typeof p.entity_type === 'string' ? p.entity_type : null;
        const eid = p.entity_id;
        if (et === 'milestone' && num(eid) !== null) openMilestoneDetail(String(num(eid)));
        else if (et === 'activity' && num(eid) !== null) openActivityDetail(num(eid)!);
        else if (et === 'leap' && num(eid) !== null) openLeapDetail(num(eid)!);
        else if (et === 'measurement') { setNavStack([]); setTab('growth'); }
        else setShowMediaGallery(true);
        break;
      }
      case 'questionnaire_answered':
        setNavStack([]);
        setTab('journey');
        break;
      case 'member_joined':
        setShowProfile(true);
        break;
    }
  };

  // Deep-link / Web Push click navigation: URL query params or SW postMessages
  useEffect(() => {
    if (!baby?.id) return;

    const navigateFromPayload = (payload: Record<string, unknown>) => {
      const eType = typeof payload.event_type === 'string' ? payload.event_type : '';
      handleOpenEvent({
        id: 'deeplink',
        baby_id: baby.id,
        actor_user_id: null,
        actor_name: null,
        event_type: (eType || 'unknown') as BabyEventRow['event_type'],
        payload,
        created_at: new Date().toISOString(),
      });
    };

    // 1. Process URL search params on boot
    try {
      const url = new URL(window.location.href);
      const milestone = url.searchParams.get('milestone');
      const activity = url.searchParams.get('activity');
      const tabParam = url.searchParams.get('tab');
      const gallery = url.searchParams.get('gallery');
      const notifs = url.searchParams.get('notifications');

      let handled = false;
      if (milestone) {
        openMilestoneDetail(milestone);
        url.searchParams.delete('milestone');
        handled = true;
      } else if (activity && /^\d+$/.test(activity)) {
        openActivityDetail(Number(activity));
        url.searchParams.delete('activity');
        handled = true;
      } else if (tabParam && ['today', 'list', 'activities', 'timeline', 'journey', 'growth', 'ai', 'leaps'].includes(tabParam)) {
        setTab(tabParam as TabId);
        url.searchParams.delete('tab');
        handled = true;
      } else if (gallery === '1') {
        setShowMediaGallery(true);
        url.searchParams.delete('gallery');
        handled = true;
      } else if (notifs === '1') {
        setShowNotifications(true);
        url.searchParams.delete('notifications');
        handled = true;
      }

      if (handled) {
        window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      }
    } catch {
      /* no-op */
    }

    // 2. Listen to SW postMessage for notifications clicked while app is already open
    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NAVIGATE_NOTIFICATION' && event.data?.data) {
        navigateFromPayload(event.data.data);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMessage);
      return () => {
        navigator.serviceWorker.removeEventListener('message', onSwMessage);
      };
    }
  }, [baby?.id]);

  const [sheetMid, setSheetMid] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<MilestoneStatus>('soon');
  const [dateChoice, setDateChoice] = useState('today');
  const [customDate, setCustomDate] = useState(todayIso());

  // Shared milestones query — used both for the open status sheet and (via
  // TanStack Query's dedup) by MilestoneList / Timeline / Today / Dashboard.
  //
  // IMPORTANT: hook calls must be unconditional and in stable order on every
  // render (Rules of Hooks). Keep these calls ABOVE the early returns below so
  // the hook count doesn't change when the baby loads in.
  const milestonesQ = useBabyMilestones(baby?.id ?? null);
  const updateStatus = useUpdateMilestoneStatus();

  // Open the status sheet pre-filled with the milestone's current status so the
  // user adjusts from where it is rather than always starting at "not started".
  const openStatus = (id: string) => {
    const row = milestonesQ.data?.milestones.find((m) => String(m.milestone_id) === id);
    setSheetMid(id);
    setDraftStatus(row ? uiStatus(row.status) : 'soon');
    const { choice, customDate: initialDate } = dateChoiceForIso(row?.achieved_date);
    setDateChoice(choice);
    setCustomDate(initialDate);
  };

  // ── Loading / empty states ─────────────────────────────────────────
  if (showCatchup && milestonesQ.isLoading) return <Splash />;

  const onSwitchBaby = (id: string) => {
    setActiveId(id);
    setShowProfile(false);
    // Force all baby-scoped queries to refetch so screens show the new baby's data immediately.
    qc.invalidateQueries({ queryKey: ['babies', id] });
  };

  const screen = (() => {
    if (showMediaGallery) {
      return (
        <BabyMediaGallery
          baby={baby}
          onBack={() => setShowMediaGallery(false)}
          onAddPhotos={() => setShowPhotoUpload(true)}
        />
      );
    }
    if (navStack.length > 0) {
      const top = navStack[navStack.length - 1];
      if (top.type === 'activity') {
        return (
          <ActivityDetail
            baby={baby}
            activityId={top.id}
            onBack={handlePopDetail}
            onOpenMedia={() => setShowMediaGallery(true)}
            onOpenMilestone={openMilestoneDetail}
          />
        );
      }
      if (top.type === 'milestone') {
        return (
          <MilestoneDetail
            milestoneId={top.id}
            baby={baby}
            onBack={handlePopDetail}
            onOpenStatus={openStatus}
            onOpenMedia={() => {
              setShowMediaGallery(true);
              handlePopDetail();
            }}
            initialShowTiming={top.showTiming}
            onOpenLeap={openLeapDetail}
            onOpenActivities={() => {
              setNavStack([]);
              setTab('activities');
            }}
            onOpenMilestone={openMilestoneDetail}
            onOpenActivity={openActivityDetail}
          />
        );
      }
      if (top.type === 'leap') {
        return (
          <LeapDetail
            leapId={top.id}
            baby={baby}
            onBack={handlePopDetail}
            onOpenMilestone={openMilestoneDetail}
            onOpenActivity={openActivityDetail}
            onOpenStatus={openStatus}
          />
        );
      }
      if (top.type === 'activityCategory') {
        return (
          <ActivityCategoryList
            category={top.id}
            baby={baby}
            onBack={handlePopDetail}
            onOpenDetail={openActivityDetail}
          />
        );
      }
    }
    if (showDashboard) {
      return (
        <Dashboard
          baby={baby}
          onClose={() => setShowDashboard(false)}
          onOpenDetail={(id) => openMilestoneDetail(id)}
        />
      );
    }
    switch (tab) {
      case 'today':
        return (
          <Today
            baby={baby}
            onOpenProfile={() => setShowProfile(true)}
            onOpenNotifications={() => setShowNotifications(true)}
            onOpenDetail={openMilestoneDetail}
            onOpenDashboard={() => setShowDashboard(true)}
            onOpenActivity={openActivityDetail}
            onOpenActivities={() => setTab('activities')}
            onAddPhotos={() => setShowPhotoUpload(true)}
            onSwitchTab={setTab}
          />
        );
      case 'activities':
        return (
          <Activities
            baby={baby}
            onOpenDetail={openActivityDetail}
            onOpenProfile={() => setShowProfile(true)}
            onOpenCategory={openActivityCategory}
          />
        );
      case 'list':
        return (
          <MilestoneList
            baby={baby}
            onOpenDetail={openMilestoneDetail}
            onOpenProfile={() => setShowProfile(true)}
            onOpenLeap={openLeapDetail}
          />
        );
      case 'journey':
        return (
          <JourneyTimeline
            baby={baby}
            onOpenDetail={openMilestoneDetail}
            onOpenActivity={openActivityDetail}
            onNavigateToday={() => setTab('today')}
            onNavigateGrowth={() => setTab('growth')}
            onOpenProfile={() => setShowProfile(true)}
            onOpenMedia={() => setShowMediaGallery(true)}
            onAddPhotos={() => setShowPhotoUpload(true)}
          />
        );
      case 'timeline':
        return <Timeline baby={baby} onOpenDetail={openMilestoneDetail} />;
      case 'leaps':
        return <Leaps baby={baby} onOpenLeap={openLeapDetail} onOpenProfile={() => setShowProfile(true)} />;
      case 'growth':
        return <Growth baby={baby} onOpenProfile={() => setShowProfile(true)} />;
      case 'ai':
        return <AiChat baby={baby} onOpenProfile={() => setShowProfile(true)} />;
    }
  })();

  const sheetRow = sheetMid
    ? milestonesQ.data?.milestones.find((m) => String(m.milestone_id) === sheetMid)
    : undefined;
  const sheetMilestone = sheetRow ? rowToMilestone(sheetRow) : null;

  // Map server rows → UI baby shape for the ProfileSheet baby switcher.
  // Mark the active one with `current: true`. Until per-baby screens are
  // wired in later phases, the ProfileSheet falls back to fixtures if the
  // server hasn't returned anything yet.
  const profileBabies = babies.length > 0
    ? babies.map((row: BabyRow) => ({
        ...rowToBaby(row),
        current: row.id === activeId,
      }))
    : fixtureBabies;

  if (showCatchup) {
    return (
      <Suspense fallback={<Splash />}>
        <MilestoneCatchup
          baby={baby}
          milestones={milestonesQ.data?.milestones ?? []}
          onComplete={() => setShowCatchup(false)}
        />
      </Suspense>
    );
  }

  return (
    <>
      <AppShell
        tab={tab}
        onTab={(id) => {
          // Leaving a drill-in view when a bottom/sidebar tab is tapped.
          setNavStack([]);
          setShowDashboard(false);
          setShowMediaGallery(false);
          setTab(id);
        }}
        baby={baby}
        onOpenProfile={() => setShowProfile(true)}
      >
        <Suspense fallback={<div className="flex items-center justify-center p-16 text-sm text-ink-3">Loading…</div>}>
          {screen}
        </Suspense>
      </AppShell>

      <ProfileSheet
        open={showProfile}
        baby={baby}
        babies={profileBabies}
        onSwitchBaby={onSwitchBaby}
        onAddBaby={(data) =>
          createBaby.mutate(
            { name: data.name, date_of_birth: data.dob, gender: data.gender },
            {
              onSuccess: (res: { baby: BabyRow }) => {
                const newBaby = rowToBaby(res.baby);
                sessionStorage.setItem('just_signed_up_baby_' + newBaby.id, 'true');
                setActiveId(newBaby.id!);
                setShowProfile(false);
              },
              onError: (e: any) => console.error(e),
            },
          )
        }
        onEditBaby={(data) => {
          if (!baby.id) return;
          updateBaby.mutate(
            { babyId: baby.id, name: data.name, date_of_birth: data.dob, gender: data.gender },
            { onError: (e: any) => console.error(e) },
          );
        }}
        onSignOut={async () => {
          setShowProfile(false);
          await signOut();
        }}
        onClose={() => setShowProfile(false)}
        onOpenMedia={() => setShowMediaGallery(true)}
        onOpenSettings={() => setShowSettings(true)}
        adding={createBaby.isPending}
        editing={updateBaby.isPending}
      />

      <SettingsSheet
        open={showSettings}
        gender={gender}
        setGender={setGender}
        baby={baby}
        onClose={() => setShowSettings(false)}
      />

      <NotificationsSheet
        open={showNotifications}
        baby={baby}
        onClose={() => setShowNotifications(false)}
        onOpenEvent={handleOpenEvent}
      />

      <JoinBabySheet
        open={pendingJoin !== null}
        initialCode={pendingJoin || null}
        onClose={() => {
          clearPendingInvite();
          setPendingJoin(null);
        }}
        onJoined={(id) => {
          clearPendingInvite();
          setPendingJoin(null);
          setActiveId(id);
        }}
      />

      <PhotoUploadSheet
        open={showPhotoUpload}
        onClose={() => setShowPhotoUpload(false)}
        baby={baby}
      />

      {sheetMilestone && (
        <StatusSheet
          open
          milestone={sheetMilestone}
          value={draftStatus}
          onChange={setDraftStatus}
          dateChoice={dateChoice}
          setDateChoice={setDateChoice}
          customDate={customDate}
          setCustomDate={setCustomDate}
          minDate={baby.dobIso}
          babyId={baby.id}
          saving={updateStatus.isPending}
          onSave={() => {
            if (!baby.id || !sheetMid) return;
            updateStatus.mutate(
              {
                babyId: baby.id,
                milestoneId: Number(sheetMid),
                status: serverStatus(draftStatus),
                // Only stamp an achieved date when the milestone is achieved;
                // otherwise clear it (e.g. moving achieved → in progress).
                achieved_date: draftStatus === 'done' ? isoForDateChoice(dateChoice, customDate) : null,
              },
              { onSuccess: () => setSheetMid(null) },
            );
          }}
          onClose={() => setSheetMid(null)}
        />
      )}
    </>
  );
}

/* ── Fallback screens ────────────────────────────────────────────────── */

interface NoBabiesProps {
  onAddBaby: (data: BabyFormData) => void;
  onJoinBaby: () => void;
  adding?: boolean;
  email: string | null;
}

function NoBabies({ onAddBaby, onJoinBaby, adding, email }: NoBabiesProps) {
  const [open, setOpen] = useState(false);
  const t = useT();
  return (
    <>
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-bg px-6 text-center text-ink">
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
          {t('auth.welcomeBack', 'Welcome back')}
        </p>
        <h1 className="font-serif text-3xl tracking-tight">
          {t('auth.noBabyTitle', 'No baby on file yet.')}
        </h1>
        <p className="max-w-sm text-sm leading-relaxed text-ink-2">
          {t('auth.noBabyDesc', 'Add your first baby to start tracking milestones and growth.')}
        </p>
        {email && <p className="text-xs text-ink-3">{t('auth.signedInAs', 'Signed in as {email}').replace('{email}', email)}</p>}
        <button
          onClick={() => setOpen(true)}
          disabled={adding}
          className="mt-2 inline-flex min-h-12 items-center justify-center gap-2 rounded-3xl bg-ink px-6 text-[14px] font-semibold text-bg disabled:opacity-50"
        >
          {adding ? t('common.adding', 'Adding…') : t('auth.addYourBaby', 'Add your baby')}
        </button>
        <button
          onClick={onJoinBaby}
          className="text-[13px] font-semibold text-ink-2 underline"
        >
          {t('profile.joinBaby', 'Join a baby')}
        </button>
      </div>

      <BabyFormSheet
        open={open}
        mode="add"
        onSave={(data) => {
          onAddBaby(data);
          setOpen(false);
        }}
        onClose={() => setOpen(false)}
        saving={adding}
      />
    </>
  );
}

interface AuthErrorProps {
  error: unknown;
  onSignOut: () => Promise<void>;
}

function AuthError({ error, onSignOut }: AuthErrorProps) {
  const t = useT();
  const detail = error instanceof ApiError
    ? `HTTP ${error.status}`
    : error instanceof Error
    ? error.message
    : 'Unknown error';
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-bg px-6 text-center text-ink">
      <p className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
        {t('auth.errorTitle', "Couldn't load your account")}
      </p>
      <h1 className="font-serif text-2xl tracking-tight">{detail}</h1>
      <p className="max-w-sm text-sm leading-relaxed text-ink-2">
        {t('auth.errorDesc', 'Sign out and back in to retry — if the problem persists, the API may be down.')}
      </p>
      <button
        onClick={onSignOut}
        className="mt-2 min-h-12 rounded-3xl border border-line bg-surface px-5 text-[14px] font-semibold text-ink"
      >
        {t('auth.signOut', 'Sign out')}
      </button>
    </div>
  );
}
